/**
 * Effort vs attention — the scoping rule (Planning Resource Model PRD, Part C §6.1).
 *
 * Capacity planning presumes work units are *effort*; many priority categories
 * are *attention* — people to develop, risks to watch. Wes's own formulation on
 * the 2026-08-24 call: you would never run the planning engine on a prioritized
 * list of people or risks, unless prioritizing those risks carries resource
 * commitments.
 *
 * This boundary is how non-work priorities are served — by *not entering* the
 * engine — rather than by generalising the engine to serve them (owner ruling 2).
 *
 * Pure: no React, no Supabase. The IO wrapper lives in planningEngineService.
 */

/**
 * Item categories that rank attention rather than work.
 *
 * Deliberately narrow. The derivation is a hint; the explicit `plannable` flag
 * is the authority. A category is only listed here when planning it as effort
 * would be a category error rather than merely unusual — which is why
 * `opportunity` and `problem` are absent: those routinely become work.
 */
export const ATTENTION_ITEM_CATEGORIES: ReadonlySet<string> = new Set(['risk']);

export type PlannabilityBasis =
  /** The list carries an explicit owner decision. */
  | 'explicit_flag'
  /** Derived from the list's declared item category. */
  | 'category'
  /** Measured: at least some members resolve an effort estimate. */
  | 'effort_bearing_items'
  /** Measured: not one member resolves an effort estimate. */
  | 'no_effort_bearing_items'
  /** Nothing to measure yet — an empty or unresolved scope. */
  | 'unmeasured';

export interface ScopePlannability {
  plannable: boolean;
  basis: PlannabilityBasis;
  /** One sentence naming why, for the empty state and the scope picker. */
  reason: string;
  effortBearingCount: number;
  itemCount: number;
}

/**
 * The empty state a non-plannable scope gets instead of a zero-capacity run.
 * Explanatory, not an error dump — the binding is not a mistake, it is a
 * category the engine does not read.
 */
export const NON_PLANNABLE_EMPTY_STATE =
  'This list ranks things to pay attention to, not work to resource. The planning ' +
  'engine reads effort. If items here imply resource commitments, capture those as ' +
  'items with effort.';

/**
 * Resolves whether a bound Engine Input Scope is plannable.
 *
 * Precedence — most authoritative first:
 *   1. the owner's explicit flag on the list
 *   2. the list's declared item category
 *   3. whether its members bear effort at all
 *
 * Unknown is plannable: an unmeasured or unlabelled scope behaves exactly as it
 * does today. This check only ever stops a run that would have been meaningless.
 */
export function resolvePlannability(args: {
  /** `item_pools.plannable` — null when the owner has not decided. */
  explicitFlag: boolean | null | undefined;
  /** `item_pools.required_item_category`, when the list declares one. */
  requiredItemCategory: string | null | undefined;
  /** Members that resolve an effort estimate through the item-effort chain. */
  effortBearingCount: number;
  /** Members in scope. */
  itemCount: number;
}): ScopePlannability {
  const { explicitFlag, requiredItemCategory, effortBearingCount, itemCount } = args;

  if (explicitFlag === true || explicitFlag === false) {
    return {
      plannable: explicitFlag,
      basis: 'explicit_flag',
      reason: explicitFlag
        ? 'Marked as work to resource on the list.'
        : 'Marked as attention rather than work on the list.',
      effortBearingCount,
      itemCount,
    };
  }

  const category = requiredItemCategory?.trim().toLowerCase();
  if (category && ATTENTION_ITEM_CATEGORIES.has(category)) {
    return {
      plannable: false,
      basis: 'category',
      reason: `This list is scoped to ${category} items, which rank attention rather than effort.`,
      effortBearingCount,
      itemCount,
    };
  }

  if (itemCount === 0) {
    return {
      plannable: true,
      basis: 'unmeasured',
      reason: 'Nothing resolved in this scope yet.',
      effortBearingCount,
      itemCount,
    };
  }

  if (effortBearingCount === 0) {
    return {
      plannable: false,
      basis: 'no_effort_bearing_items',
      reason: `None of the ${itemCount} item${itemCount === 1 ? '' : 's'} in this scope carries an effort estimate or a size the engine can infer one from.`,
      effortBearingCount,
      itemCount,
    };
  }

  return {
    plannable: true,
    basis: 'effort_bearing_items',
    reason: `${effortBearingCount} of ${itemCount} item${itemCount === 1 ? '' : 's'} in this scope carry effort.`,
    effortBearingCount,
    itemCount,
  };
}
